import clsx from '@vangst/lib/clsx'
import useCompany from '@vangst/services/oogst/company/useCompany'
import useCompanyFollowers, {
  CompanyFollowersOptions,
  CompanyFollowersVars,
} from '@vangst/services/oogst/company/useCompanyFollowers'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { Fragment, memo } from 'react'
import { MdArrowDownward, MdPerson } from 'react-icons/md'
import Spinner from '../../components/assets/Spinner'
import EmptyState from '../../components/feedback/EmptyState'
import ClickyExplore from '../../components/navigation/ClickyExplore'
import ClickyLink from '../../components/navigation/ClickyLink'
import Counter from '../../components/views/Counter'
import MemberRenderer from '../member/MemberRenderer'
import CompanyRenderer from './CompanyRenderer'
import FollowEditSignup from './FollowEditSignup'

type PropsType = React.HTMLAttributes<HTMLDivElement> & {
  readonly as: 'avatar' | 'card' | 'slip'
  readonly at: 'infinite' | 'preview' | 'sidebar'
  readonly classNameList?: string
  readonly take?: number
  readonly variables: CompanyFollowersVars
  readonly options?: CompanyFollowersOptions
}

function CompanyFollowersGrid(props: PropsType) {
  const { authenticating, authenticated } = useViewer()
  const unauthenticated = !authenticating && !authenticated

  const { as, at, classNameList, variables, options, take, ...rest } = props
  const company = useCompany({ displayname: variables.displayname })
  const { data, isFetching, isZero, pagination, uri } = useCompanyFollowers(
    variables,
    { ...options, enabled: authenticated },
  )

  const canTake = take != null && (at === 'preview' || at === 'sidebar')
  const pages = canTake && data?.pages ? [data.pages[0]] : data?.pages
  const canEdit = company?.data?.getClient?.permissions.edit

  return (
    <div {...rest}>
      {at === 'sidebar' && (
        <>
          {authenticated && (
            <h3 className="mb-1 mt-8 text-sm">
              Followers{' '}
              <span>
                (<Counter to={pagination.totalCount} />)
              </span>
              :
            </h3>
          )}
          {authenticated && (
            <p className="relative mb-4 text-sm">
              <ClickyLink
                className="link-orange relative my-2 ml-4 block underline before:absolute before:-left-3 before:block before:w-2 before:text-orange before:content-['\2022']"
                href={uri}
              >
                View Followers
              </ClickyLink>
              {isFetching && (
                <Spinner
                  className="absolute right-0 top-0"
                  width={16}
                  height={16}
                />
              )}
            </p>
          )}
        </>
      )}

      {at === 'infinite' && (
        <p className="relative mb-4">
          <span className="mr-2">Results</span>
          {authenticated && <Counter to={pagination.totalCount} />}
          {isFetching && (
            <Spinner
              className="absolute right-0 top-0"
              width={16}
              height={16}
            />
          )}
        </p>
      )}

      <ul className={clsx('mt-8 min-h-40 w-full', !isZero && classNameList)}>
        {isZero && canEdit && (
          <li>
            <EmptyState className="sm:min-h-60" icon={MdPerson}>
              {unauthenticated && (
                <span>
                  <ClickyLink className="link-blue-dark" href={Routes.SIGN_UP}>
                    Join
                  </ClickyLink>
                  <span> or </span>
                  <ClickyLink className="link-blue-dark" href={Routes.SIGN_IN}>
                    sign in
                  </ClickyLink>
                  <span> to view the member and company followers.</span>
                </span>
              )}
              {canEdit && (
                <span>
                  <ClickyLink className="link-orange" href={Routes.MEMBERS}>
                    Search the Vangst network
                  </ClickyLink>{' '}
                  and invite other members to follow your company.
                </span>
              )}
              {!canEdit && authenticated && (
                <FollowEditSignup as="link" variables={variables} />
              )}
            </EmptyState>
          </li>
        )}

        {pages?.map((page, i) => {
          const list = page.getClient.followers.edges
          const edges = canTake ? list?.slice(0, take) : list
          return (
            <Fragment key={`company-followers-renderer-page-${i}`}>
              {edges?.map((edge) => {
                const node = edge?.node
                return (
                  <li key={node?.id || `item-${i}`}>
                    {node?.__typename === 'Client' ? (
                      <CompanyRenderer
                        as={as}
                        variables={{ displayname: node.displayname }}
                        options={{ initialData: { getClient: node } }}
                      />
                    ) : node?.__typename === 'User' ? (
                      <MemberRenderer
                        as={as}
                        variables={{ username: node.username }}
                        options={{ initialData: { getUser: node } }}
                      />
                    ) : null}
                  </li>
                )
              })}
              {!isZero && at === 'infinite' && (
                <li className="col-span-full my-4 border-b pb-2 text-right text-sm">
                  Page {i + 1} of {pagination?.totalPages}
                </li>
              )}
            </Fragment>
          )
        })}
      </ul>
      {at === 'infinite' && pagination.nextHref && pagination.nextPage && (
        <ClickyExplore
          as="icon-text"
          className={clsx('w-full', !isFetching && 'motion-south')}
          href={pagination.nextHref}
          icon={isFetching ? Spinner : MdArrowDownward}
          onClick={pagination.nextPage}
        >
          {isFetching ? 'Loading...' : 'Load Next Page'}
        </ClickyExplore>
      )}
    </div>
  )
}

export default memo(CompanyFollowersGrid)
